import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  DEFAULT_META_DESCRIPTION,
  formatPageTitle,
  getSiteOrigin,
  SITE_NAME,
} from '@/utils/seo';

const OG_DEFAULT_IMAGE =
  'https://res.cloudinary.com/dh0lo5l66/image/upload/v1774176044/og-v12.png';

/**
 * @param {object} props
 * @param {string} props.title – sidrubrik (suffix "| ByggFynd" läggs till automatiskt)
 * @param {string} [props.description]
 * @param {string} [props.canonicalPath] – t.ex. "/" eller "/produkter?category=el"
 * @param {string} [props.ogImage]
 * @param {string} [props.ogType] – "website" | "product" m.m.
 * @param {object|null} [props.jsonLd] – ett JSON-LD-objekt
 * @param {React.ReactNode} [props.children] – extra meta-taggar
 */
export function SeoMeta({
  title,
  description,
  canonicalPath,
  ogImage,
  ogType = 'website',
  jsonLd,
  children,
}) {
  const origin = getSiteOrigin();
  const fullTitle = formatPageTitle(title);
  const desc = (description && description.trim()) || DEFAULT_META_DESCRIPTION;
  const canonical =
    canonicalPath != null && origin
      ? `${origin}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`
      : null;
  const image = ogImage || OG_DEFAULT_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="sv" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={ogType} />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      {image ? <meta property="og:image" content={image} /> : null}
      <meta property="og:locale" content="sv_SE" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {image ? <meta name="twitter:image" content={image} /> : null}
      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
      {children}
    </Helmet>
  );
}

/** Sätt REACT_APP_SEO_NOINDEX=true före lansering om sajten inte ska indexeras. */
export function SeoNoIndex() {
  if (process.env.REACT_APP_SEO_NOINDEX !== 'true') return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}
